<template v-if="histories">
    <div class="nav-pills justify-content-start">
        <template v-for="history in histories" >
            <div :class="is_selected(history)"
                 class="nav-item nav-link d-inline-flex grey flex-row align-items-middle justify-content-between m-2"
                 :style="'background-color: ' + is_active(history)"
                 @click="choose(history)">
                <div  class="flex-column pe-2">
                    <img :src="history.image" alt="" class="img-thumbnail" style="max-height: 60px">
                </div>
                <div class="flex-column">
                    <h4 class="text-primary m-1">{{ title(history) }}</h4>
                    <h5 class="text-secondary m-1" v-html="renderSerialLink(history)"></h5>
                    <div class="text-grey-50 italic m-1">{{ start_date(history) }} {{ $t('to') }} {{ end_date(history) }}</div>
                </div>
                <div class="dropdown nav justify-content-end align-items-start">
                    <button type="button" class="btn btn-sm" data-bs-toggle="dropdown" aria-expanded="true">
                        <i class="bi bi-three-dots-vertical"></i>
                    </button>

                    <div
                        class="dropdown-menu dropdown-menu-end"
                        aria-labelledby="tablist"
                        data-popper-placement="bottom-start"
                    >
                        <span class="dropdown-header">{{$t('View Selection')}}</span>
                        <a
                            v-if="!history.pin && !history.end_date && checkAddPinPermissions()"
                            style="text-transform: capitalize"
                            data-bs-toggle="pill"
                            class="dropdown-item"
                            @click="()=> emits('machine:add-pin', history)"
                        >
                            <i
                                class="bi bi-plus-circle"
                            />
                            {{$t('Add Pin Number')}}
                        </a>
                        <a
                            style="text-transform: capitalize"
                            data-bs-toggle="pill"
                            class="dropdown-item"
                            v-if="hasPermission(auth_user.can, 'update.device')"
                            @click="()=> emits('machine:update', history)"
                        >
                            <i
                                class="bi bi-pen"
                            />
                            {{$t('Edit/End')}}
                        </a>
                        <a
                            data-bs-toggle="pill"
                            class="dropdown-item"
                            @click.prevent="emits('machine:icode', history)">
                            <i class="bi bi-table"></i>
                            {{ $t('iCode Reports') }}
                        </a>
                        <a
                            style="text-transform: capitalize"
                            data-bs-toggle="pill"
                            class="dropdown-item"
                            v-if="hasPermission(auth_user.can, 'delete.device')"
                            @click="()=> emits('machine:delete', history)"
                        >
                            <i
                                class="bi bi-trash3"
                            />
                            {{$t('Delete')}}
                        </a>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {trans as $t} from "@/vue/composables/missingTranslations";
import usePermissions from "@/vue/composables/usePermissions";
import {useUserStore} from "@/vue/store/userStore";
import useDates, {DateFormat} from "@/libs/dates/useDates";

const { hasPermission } = usePermissions();

const props = defineProps({
    histories: {
        required: true,
    },
    context: {
        type: String,
        default: 'patient',
    },
    auth_user: {
        type: [Object, null],
        required: true,
    },
    patient: {
        type: [Object, null],
        required: true,
    }
})

const userStore = useUserStore();
const auth_user = computed(() => userStore.user);

const {format} = useDates();

const emits = defineEmits(['update:modelValue', 'machine:delete', 'machine:update', 'machine:icode']);
const selected = ref(null);
function data() {
    return {
        modelValue: {}
    }
}
onMounted(() => choose(props.histories[0]));
function choose(history) {
    if (selected.value && history.history_id === selected.value.history_id) {
        return;     // Already selected
    }
    selected.value = history;
    emits('update:modelValue', history);
}
function is_selected(history) {
    if (selected.value && history.history_id === selected.value.history_id) {
        return 'border border-primary border-2';
    }
    return 'border border-secondary border-2';
}
function is_active(history) {
    if (!history.is_assigned_to_patient) {
        return '#f0f0f0';
    }
    return '';
}
function title(history) {
    if (props.context === 'patient') {
        return history.model;
    }
    return history.patient_name;
}
function subtitle(history) {
    return history.serial;
}

const renderSerialLink = (history) => {
    if (history) {
        if (auth_user.value && hasPermission(auth_user.value.can, 'view.org.user.device')) {
            return `<a href="/org-device/${history.serial}/${history.guid}" target="_blank" class="text-secondary">
                        ${history.serial}
                    </a>`;
        }
        else if (auth_user.value && hasPermission(auth_user.value.can, 'viewAny.device')) {
            return `<a href="/devices/${history.serial}/${history.guid}" target="_blank" class="text-secondary">
                        ${history.serial}
                    </a>`;
        } else {
            return history.serial || null;
        }
    }
}

function start_date(history) {
    return format(history.start_date, DateFormat.LongDate);
}

function end_date(history) {
    if (history.end_date) {
        return format(history.end_date, DateFormat.LongDate);
    }

    return $t('present');
}

const checkAddPinPermissions = () => {
    if (props.auth_user) {
        const isClinicalOrgOnly = props.auth_user.roles.length === 1 && props.auth_user.roles.filter(role => {
            return role.name === 'org.physician' || role.name === 'org.clinical.staff' || role.name === 'org.clinical.admin';
        }).length > 0;
        if (isClinicalOrgOnly) {
            return hasPermission(props.auth_user.can, 'update.device.pin') &&
                props.patient?.organization?.settings?.settings?.physician_can_add_missing_pin;
        }
        return hasPermission(props.auth_user.can, 'update.device.pin');
    }
}

const checkAllPermissions = () => {
    if (props.auth_user) {
        return checkAddPinPermissions() || hasPermission(props.auth_user.can, 'update.device') ||
            hasPermission(props.auth_user.can, 'delete.device');
    }
    return false;
}
</script>
